import { useEffect, useMemo, useState, useCallback } from "react";
import axiosDefault from "axios";
import {
  Panel,
  Stack,
  Text,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  TextField,
  PrimaryButton,
  DefaultButton,
  IconButton,
  MessageBar,
  MessageBarType,
  Spinner,
  Separator,
  PanelType,
} from "@fluentui/react";

function slugify(input = "") {
  return String(input)
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function TagEditor(props) {
  const axios = props.axiosInstance || axiosDefault;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [tags, setTags] = useState([]);
  const [filter, setFilter] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const selectedTag = useMemo(
    () => tags.find((t) => String(t.id) === String(selectedId)) || null,
    [tags, selectedId]
  );

  // form state (used for both create + edit)
  const [mode, setMode] = useState("create"); // "create" | "edit"
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);

  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const resetMessages = () => {
    setErr("");
    setOk("");
  };

  const loadTags = useCallback(async () => {
    setLoading(true);
    resetMessages();
    try {
      const { data } = await axios.get("/api/tags");
      setTags(Array.isArray(data) ? data : data?.tags || []);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "Failed to load tags.");
    } finally {
      setLoading(false);
    }
  }, [axios]);

  useEffect(() => {
    if (props.isOpen) loadTags();
  }, [props.isOpen, loadTags]);

  // When switching selection, populate edit form
  useEffect(() => {
    if (!selectedTag) return;
    setMode("edit");
    setName(selectedTag.name || "");
    setSlug(selectedTag.slug || "");
    setSlugTouched(true);
  }, [selectedTag]);

  // Auto-generate slug from name (only if user hasn’t manually edited slug)
  useEffect(() => {
    if (mode !== "create") return;
    if (slugTouched) return;
    setSlug(slugify(name));
  }, [name, mode, slugTouched]);

  const filteredTags = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter((t) => {
      const n = String(t.name || "").toLowerCase();
      const s = String(t.slug || "").toLowerCase();
      return n.includes(q) || s.includes(q);
    });
  }, [tags, filter]);

  const columns = useMemo(
    () => [
      {
        key: "name",
        name: "Name",
        fieldName: "name",
        minWidth: 140,
        isResizable: true,
      },
      {
        key: "slug",
        name: "Slug",
        fieldName: "slug",
        minWidth: 140,
        isResizable: true,
      },
      // {
      //   key: "actions",
      //   name: "",
      //   minWidth: 44,
      //   maxWidth: 44,
      //   onRender: (item) => (
      //     <IconButton
      //       iconProps={{ iconName: "Delete" }}
      //       title="Delete tag"
      //       ariaLabel="Delete tag"
      //       onClick={(ev) => {
      //         ev?.stopPropagation?.();
      //         onDelete(item);
      //       }}
      //     />
      //   ),
      // },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags]
  );

  const startCreate = () => {
    resetMessages();
    setSelectedId(null);
    setMode("create");
    setName("");
    setSlug("");
    setSlugTouched(false);
  };

  const validate = () => {
    if (!name.trim()) return "Name is required.";
    if (!slug.trim()) return "Slug is required.";
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug.trim()))
      return "Slug must be lowercase letters/numbers and hyphens only.";
    return "";
  };

  const onSave = async () => {
    resetMessages();
    const v = validate();
    if (v) return setErr(v);

    setSaving(true);
    try {
      if (mode === "create") {
        const { data } = await axios.post("/api/tags", {
          name: name.trim(),
          slug: slug.trim(),
        });
        setOk("Tag created.");
        // keep list fresh
        await loadTags();
        // select new tag if returned
        if (data?.id) setSelectedId(data.id);
        else startCreate();
      } else {
        if (!selectedTag?.id) throw new Error("No tag selected to update.");
        await axios.put(`/api/tags/${selectedTag.id}`, {
          name: name.trim(),
          slug: slug.trim(),
        });
        setOk("Tag updated.");
        await loadTags();
      }
    } catch (e) {
      setErr(
        e?.response?.data?.error ||
          e?.message ||
          "Save failed. Check server logs."
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if(selectedId === null) cancelAction();
  }, [selectedId]);

  const cancelAction = () => {
    setSelectedId(null);
    setMode('create');
    setName("");
    setSlug("");
  }

  const onDelete = async (tag) => {
    resetMessages();
    const id = tag?.id;
    if (!id) return;

    const confirmed = window.confirm(
      `Delete tag "${tag.name || tag.slug}"?\n\nThis can’t be undone.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await axios.delete(`/api/tags/${id}`);
      setOk("Tag deleted.");
      setSelectedId(null);
      startCreate();
      await loadTags();
    } catch (e) {
      setErr(
        e?.response?.data?.error ||
          e?.message ||
          "Delete failed. Check server logs."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel
      isOpen={props.isOpen}
      onDismiss={() => props.onDismiss?.()}
      closeButtonAriaLabel="Close"
      isLightDismiss
      type={PanelType.extraLarge}
    >
      <Stack tokens={{ childrenGap: 12 }}>
        <Text variant="xLarge">Tag Editor</Text>

        {(err || ok) && (
          <MessageBar
            messageBarType={err ? MessageBarType.error : MessageBarType.success}
            onDismiss={() => {
              setErr("");
              setOk("");
            }}
            isMultiline={false}
          >
            {err || ok}
          </MessageBar>
        )}

        <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { minHeight: 520 } }}>
          {/* LEFT: list */}
          <Stack styles={{ root: { width: "45%", minWidth: 360 } }} tokens={{ childrenGap: 8 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <Text variant="large">Tags</Text>
              <Stack horizontal tokens={{ childrenGap: 8 }}>
                <DefaultButton
                  text="New"
                  onClick={startCreate}
                  disabled={loading || saving}
                />
                <DefaultButton
                  text="Refresh"
                  onClick={loadTags}
                  disabled={loading || saving}
                />
              </Stack>
            </Stack>

            <TextField
              placeholder="Search name or slug…"
              value={filter}
              onChange={(_, v) => setFilter(v || "")}
              disabled={loading}
            />

            {loading ? (
              <Spinner label="Loading tags..." />
            ) : (
              <DetailsList
                items={filteredTags}
                columns={columns}
                setKey="tags"
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                // onItemInvoked={(item) => setSelectedId(item.id)}
                // onActiveItemChanged={(item) => setSelectedId(item?.id ?? null)}
              />
            )}
          </Stack>

          <Separator vertical />

          {/* RIGHT: form */}
          <Stack styles={{ root: { width: "55%", minWidth: 420 } }} tokens={{ childrenGap: 10 }}>
            <Text variant="large">
              {mode === "create" ? "Create tag" : "Edit tag"}
            </Text>

            <TextField
              label="Name"
              value={name}
              onChange={(_, v) => {
                setName(v || "");
                if (mode === "create" && !slugTouched) setSlug(slugify(v || ""));
              }}
              disabled={saving}
              required
            />

            <TextField
              label="Slug"
              value={slug}
              onChange={(_, v) => {
                setSlug(v || "");
                setSlugTouched(true);
              }}
              disabled={saving}
              required
              description="Lowercase letters/numbers and hyphens only (e.g., “fish-passage”)."
            />

            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text={saving ? "Saving..." : "Save"}
                onClick={onSave}
                disabled={saving || loading}
              />
              {mode === "edit" && selectedTag?.id && (
                <DefaultButton
                  text="Cancel"
                  onClick={() => setSelectedId(null)}
                  disabled={saving || loading}
                />
              )}
            </Stack>

            {mode === "edit" && selectedTag?.id && (
              <Text variant="small" styles={{ root: { opacity: 0.75 } }}>
                Editing ID: {selectedTag.id}
              </Text>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Panel>
  );
}
